import React, { useState, useRef, useEffect } from 'react';
import SEO from '../components/SEO';
import TextareaWithLines from '../components/TextareaWithLines';
import PreWithLines from '../components/PreWithLines';

const SITE_DOMAIN = process.env.REACT_APP_SITE_DOMAIN || 'https://yourjsontools.com';

const JsonToToonPage = () => {
  const [jsonInput, setJsonInput] = useState('');
  const [toonOutput, setToonOutput] = useState('');
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Options
  const [delimiter, setDelimiter] = useState(',');
  const [indentation, setIndentation] = useState(2);
  const [showLengthMarkers, setShowLengthMarkers] = useState(true);
  
  // Stats
  const [jsonTokens, setJsonTokens] = useState(0);
  const [toonTokens, setToonTokens] = useState(0);
  
  const fileInputRef = useRef(null);
  
  // Refs for scroll syncing
  const inputRef = useRef(null);
  const outputRef = useRef(null);
  const inputLineNumbersRef = useRef(null);
  const outputLineNumbersRef = useRef(null);
  const [isInputScrolling, setIsInputScrolling] = useState(false);
  const [isOutputScrolling, setIsOutputScrolling] = useState(false);

  // Scroll synchronization
  const handleInputScroll = () => {
    if (inputLineNumbersRef.current && inputRef.current) {
      inputLineNumbersRef.current.scrollTop = inputRef.current.scrollTop;
    }
    if (!isOutputScrolling && outputRef.current && inputRef.current) {
      setIsInputScrolling(true);
      const inputScrollTop = inputRef.current.scrollTop;
      const inputScrollHeight = inputRef.current.scrollHeight;
      const inputClientHeight = inputRef.current.clientHeight;
      const outputScrollHeight = outputRef.current.scrollHeight;
      const outputClientHeight = outputRef.current.clientHeight;
      const scrollRatio = inputScrollTop / (inputScrollHeight - inputClientHeight || 1);
      const outputScrollTop = scrollRatio * (outputScrollHeight - outputClientHeight || 1);
      outputRef.current.scrollTop = outputScrollTop;
      if (outputLineNumbersRef.current) {
        outputLineNumbersRef.current.scrollTop = outputScrollTop;
      }
      setTimeout(() => setIsInputScrolling(false), 50);
    }
  };

  const handleOutputScroll = () => {
    if (outputLineNumbersRef.current && outputRef.current) {
      outputLineNumbersRef.current.scrollTop = outputRef.current.scrollTop;
    }
    if (!isInputScrolling && inputRef.current && outputRef.current) {
      setIsOutputScrolling(true);
      const outputScrollTop = outputRef.current.scrollTop;
      const outputScrollHeight = outputRef.current.scrollHeight;
      const outputClientHeight = outputRef.current.clientHeight;
      const inputScrollHeight = inputRef.current.scrollHeight;
      const inputClientHeight = inputRef.current.clientHeight;
      const scrollRatio = outputScrollTop / (outputScrollHeight - outputClientHeight || 1);
      const inputScrollTop = scrollRatio * (inputScrollHeight - inputClientHeight || 1);
      inputRef.current.scrollTop = inputScrollTop;
      if (inputLineNumbersRef.current) {
        inputLineNumbersRef.current.scrollTop = inputScrollTop;
      }
      setTimeout(() => setIsOutputScrolling(false), 50);
    }
  };

  // Estimate token count
  const estimateTokens = (text) => {
    if (!text) return 0;
    return Math.ceil(text.length / 4);
  };

  // Convert JSON to TOON format
  const jsonToToon = (data, indent = '') => {
    const indentStr = ' '.repeat(indentation);
    
    if (data === null) return 'null';
    if (data === undefined) return 'undefined';
    
    const type = typeof data;
    
    if (type === 'string') return data;
    if (type === 'number' || type === 'boolean') return String(data);
    
    if (Array.isArray(data)) {
      if (data.length === 0) return '[]';
      
      const allObjects = data.every(item => 
        typeof item === 'object' && item !== null && !Array.isArray(item)
      );
      
      if (allObjects && data.length > 0) {
        const allKeys = [...new Set(data.flatMap(obj => Object.keys(obj)))];
        const sameKeys = data.every(obj => allKeys.every(key => key in obj));
        
        if (sameKeys && allKeys.length > 0) {
          const lengthMarker = showLengthMarkers ? `[${data.length}]` : '';
          let result = `${lengthMarker}{${allKeys.join(delimiter)}}:\n`;
          
          data.forEach((obj) => {
            const values = allKeys.map(key => {
              const val = obj[key];
              if (val === null) return 'null';
              if (typeof val === 'object') return JSON.stringify(val);
              return String(val);
            });
            result += `${indent}${indentStr}${values.join(delimiter)}\n`;
          });
          
          return result.trimEnd();
        }
      }
      
      const lengthMarker = showLengthMarkers ? `[${data.length}]` : '[]';
      let result = `${lengthMarker}:\n`;
      data.forEach((item) => {
        const converted = jsonToToon(item, indent + indentStr);
        result += `${indent}${indentStr}- ${converted}\n`;
      });
      return result.trimEnd();
    }
    
    if (type === 'object') {
      const keys = Object.keys(data);
      if (keys.length === 0) return '{}';
      
      let result = '';
      keys.forEach((key) => {
        const value = data[key];
        const converted = jsonToToon(value, indent + indentStr);
        
        if (typeof value === 'object' && value !== null) {
          if (Array.isArray(value)) {
            result += `${indent}${key}${converted}\n`;
          } else {
            result += `${indent}${key}:\n${indent}${indentStr}${converted}\n`;
          }
        } else {
          result += `${indent}${key}: ${converted}\n`;
        }
      });
      return result.trimEnd();
    }
    
    return String(data);
  };

  const handleConvert = () => {
    setError('');
    setToonOutput('');
    setJsonTokens(0);
    setToonTokens(0);
    
    if (!jsonInput.trim()) {
      setError('Please enter JSON data');
      return;
    }

    try {
      const parsed = JSON.parse(jsonInput);
      const result = jsonToToon(parsed);
      
      setToonOutput(result);
      setJsonTokens(estimateTokens(jsonInput));
      setToonTokens(estimateTokens(result));
    } catch (e) {
      setError('Invalid JSON: ' + e.message);
    }
  };

  useEffect(() => {
    if (toonOutput && jsonInput) {
      try {
        const parsed = JSON.parse(jsonInput);
        const result = jsonToToon(parsed);
        setToonOutput(result);
        setToonTokens(estimateTokens(result));
      } catch (e) {
        // Ignore
      }
    }
  }, [delimiter, indentation, showLengthMarkers]);

  const handleClear = () => {
    setJsonInput('');
    setToonOutput('');
    setError('');
    setJsonTokens(0);
    setToonTokens(0);
  };

  const handleCopyOutput = () => {
    if (!toonOutput) return;
    navigator.clipboard.writeText(toonOutput).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 1500);
    });
  };

  const handleDownload = () => {
    if (!toonOutput) return;
    const blob = new Blob([toonOutput], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'output.toon';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setJsonInput(e.target.result);
      };
      reader.readAsText(file);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const sampleJson = `[
  {
    "id": 1,
    "name": "Alice Johnson",
    "age": 30,
    "city": "New York",
    "role": "Developer"
  },
  {
    "id": 2,
    "name": "Bob Smith",
    "age": 25,
    "city": "San Francisco",
    "role": "Designer"
  },
  {
    "id": 3,
    "name": "Charlie Brown",
    "age": 35,
    "city": "Chicago",
    "role": "Manager"
  }
]`;

  const handleLoadSample = () => {
    setJsonInput(sampleJson);
    setError('');
  };

  const savedPercentage = jsonTokens > 0 
    ? Math.round((1 - toonTokens / jsonTokens) * 100) 
    : 0;

  const controlButtonStyle = {
    width: '100%',
    padding: '10px',
    margin: '5px 0',
    backgroundColor: '#00BFA5',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px'
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "JSON to TOON Converter",
    "url": `${SITE_DOMAIN}/json-to-toon`,
    "applicationCategory": "DeveloperApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "description": "Convert JSON to TOON (Token-Oriented Object Notation) format. Save 30-60% tokens when working with LLMs."
  };

  return (
    <>
      <SEO 
        title="JSON to TOON Converter - Token-Efficient Format for LLMs | yourjsontools.com"
        description="Convert JSON to TOON (Token-Oriented Object Notation) format. Save 30-60% tokens when working with Large Language Models."
        keywords={["json to toon", "toon format", "token efficient", "llm optimization", "ai data format", "json converter"]}
        canonicalUrl={`${SITE_DOMAIN}/json-to-toon`}
        structuredData={jsonLd}
      />
      
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px 20px 0' }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>JSON to TOON Converter</h1>
        <p style={{ marginBottom: '20px', color: '#666' }}>
          Convert JSON to TOON (Token-Oriented Object Notation). Save 30-60% tokens when working with LLMs.
        </p>
      </div>
      
      <div 
        className="beautifier-container responsive-main-container"
        style={{ 
          display: 'flex', 
          height: 'calc(100vh - 150px)', 
          maxHeight: '800px',
          minHeight: '500px',
          margin: '0 auto', 
          padding: '20px',
          gap: '40px',
          boxSizing: 'border-box',
        }}
      >
        {/* Left Panel - JSON Input */}
        <div className="responsive-panel" style={{ 
          flex: 1, 
          position: 'relative', 
          display: 'flex', 
          flexDirection: 'column', 
          minWidth: 0,
          maxHeight: '100%',
          overflow: 'hidden'
        }}>
          <div style={{ 
            position: 'relative', 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden'
          }}>
            <TextareaWithLines
              value={jsonInput}
              onChange={e => setJsonInput(e.target.value)}
              placeholder="Enter your JSON here..."
              textareaRef={inputRef}
              lineNumbersRef={inputLineNumbersRef}
              onScroll={handleInputScroll}
              style={{ backgroundColor: '#fff' }}
              textareaProps={{ className: 'responsive-textarea' }}
              lineNumbersRefClassName="responsive-linenumbers"
            />
          </div>
        </div>

        {/* Middle Panel - Controls */}
        <div className="responsive-controls" style={{ 
          width: '220px', 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          padding: '0 10px',
          flexShrink: 0
        }}>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileUpload}
            accept=".json"
          />
          
          <button onClick={handleUploadClick} style={controlButtonStyle}>
            Upload JSON
          </button>
          
          <button onClick={handleLoadSample} style={controlButtonStyle}>
            Load Sample
          </button>
          
          <select
            value={delimiter}
            onChange={(e) => setDelimiter(e.target.value)}
            style={{
              width: '100%',
              padding: '10px',
              margin: '5px 0',
              border: '1px solid #ccc',
              borderRadius: '4px',
              boxSizing: 'border-box'
            }}
          >
            <option value=",">Delimiter: Comma</option>
            <option value="	">Delimiter: Tab</option>
            <option value="|">Delimiter: Pipe</option>
            <option value=";">Delimiter: Semicolon</option>
          </select>
          
          <select
            value={indentation}
            onChange={(e) => setIndentation(Number(e.target.value))}
            style={{
              width: '100%',
              padding: '10px',
              margin: '5px 0',
              border: '1px solid #ccc',
              borderRadius: '4px',
              boxSizing: 'border-box'
            }}
          >
            <option value={2}>Indent: 2 spaces</option>
            <option value={4}>Indent: 4 spaces</option>
            <option value={1}>Indent: 1 space</option>
          </select>
          
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: '100%',
            padding: '10px 0',
            fontSize: '14px',
            color: '#333',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={showLengthMarkers}
              onChange={(e) => setShowLengthMarkers(e.target.checked)}
              style={{ width: '16px', height: '16px' }}
            />
            Show length [#]
          </label>
          
          <button
            onClick={handleConvert}
            style={{
              ...controlButtonStyle,
              backgroundColor: '#1976D2'
            }}
          >
            Convert to TOON
          </button>
          
          <button onClick={handleDownload} style={controlButtonStyle} disabled={!toonOutput}>
            Download
          </button>
          
          <button onClick={handleClear} style={controlButtonStyle}>
            Clear All
          </button>
          
          {error && (
            <div style={{ 
              padding: '10px', 
              marginTop: '10px', 
              color: 'red',
              textAlign: 'center',
              width: '100%',
              fontWeight: 'bold',
              wordBreak: 'break-word'
            }}>
              {error}
            </div>
          )}
        </div>

        {/* Right Panel - TOON Output */}
        <div className="responsive-panel" style={{ 
          flex: 1, 
          position: 'relative', 
          display: 'flex', 
          flexDirection: 'column', 
          minWidth: 0,
          maxHeight: '100%',
          overflow: 'hidden'
        }}>
          <div style={{ 
            position: 'relative', 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden'
          }}>
            {toonOutput && (
              <button
                onClick={handleCopyOutput}
                style={{
                  position: 'absolute', top: 10, right: 10, zIndex: 10,
                  padding: '6px 14px', backgroundColor: copySuccess ? '#4CAF50' : '#00BFA5', color: 'white',
                  border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 500,
                  transition: 'background-color 0.3s',
                }}
              >
                {copySuccess ? 'Copied!' : 'Copy'}
              </button>
            )}
            <PreWithLines
              value={toonOutput}
              preRef={outputRef}
              lineNumbersRef={outputLineNumbersRef}
              onScroll={handleOutputScroll}
              style={{ backgroundColor: '#fafafa', margin: 0 }}
              preProps={{ className: 'responsive-pre' }}
              lineNumbersRefClassName="responsive-linenumbers"
            />
          </div>
        </div>
      </div>
      
      {/* Stats Section */}
      <div style={{ maxWidth: '1200px', margin: '20px auto', padding: '0 20px' }}>
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
          <div style={{
            flex: 1,
            minWidth: '200px',
            background: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            padding: '20px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px', fontWeight: 600 }}>JSON TOKENS</div>
            <div style={{ fontSize: '36px', fontWeight: 700, color: '#333' }}>{jsonTokens}</div>
          </div>
          <div style={{
            flex: 1,
            minWidth: '200px',
            background: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            padding: '20px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px', fontWeight: 600 }}>TOON TOKENS</div>
            <div style={{ fontSize: '36px', fontWeight: 700, color: '#333' }}>{toonTokens}</div>
          </div>
          <div style={{
            flex: 1,
            minWidth: '200px',
            background: '#e8f5e9',
            border: '1px solid #a5d6a7',
            borderRadius: '8px',
            padding: '20px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '12px', color: '#2e7d32', marginBottom: '8px', fontWeight: 600 }}>SAVED</div>
            <div style={{ fontSize: '36px', fontWeight: 700, color: '#2e7d32' }}>{savedPercentage}%</div>
          </div>
        </div>
      </div>
      
      <div style={{ maxWidth: '1200px', margin: '40px auto 20px', padding: '0 20px', fontSize: '15px', lineHeight: '1.6', color: '#444' }}>
        <h2 style={{ fontSize: '22px', marginBottom: '15px', color: '#333' }}>What is TOON?</h2>
        <p style={{ marginBottom: '15px' }}>
          <strong>TOON (Token-Oriented Object Notation)</strong> is a compact, human-readable data 
          serialization format designed specifically to optimize data exchange with Large Language Models (LLMs).
        </p>
        
        <h3 style={{ fontSize: '18px', margin: '20px 0 10px', color: '#333' }}>Key Features</h3>
        <ul style={{ marginBottom: '15px', paddingLeft: '20px' }}>
          <li style={{ marginBottom: '8px' }}><strong>Token Efficient:</strong> 30-60% fewer tokens compared to JSON</li>
          <li style={{ marginBottom: '8px' }}><strong>LLM Optimized:</strong> Designed for AI comprehension</li>
          <li style={{ marginBottom: '8px' }}><strong>Indentation-Based:</strong> Uses whitespace instead of braces (like YAML)</li>
          <li style={{ marginBottom: '8px' }}><strong>Tabular Arrays:</strong> Declares keys once, streams data as rows</li>
          <li style={{ marginBottom: '8px' }}><strong>Human Readable:</strong> Clean, easy to debug</li>
        </ul>

        <h3 style={{ fontSize: '18px', margin: '20px 0 10px', color: '#333' }}>Example Conversion</h3>
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '20px' }}>
          <div style={{ flex: 1, minWidth: '250px', background: '#f5f5f5', padding: '15px', borderRadius: '8px' }}>
            <h4 style={{ fontSize: '14px', color: '#666', margin: '0 0 10px 0' }}>JSON (86 tokens)</h4>
            <pre style={{ margin: 0, fontSize: '13px', whiteSpace: 'pre-wrap' }}>{`[
  {"id": 1, "name": "Alice", "age": 30},
  {"id": 2, "name": "Bob", "age": 25}
]`}</pre>
          </div>
          <div style={{ flex: 1, minWidth: '250px', background: '#e8f5e9', padding: '15px', borderRadius: '8px' }}>
            <h4 style={{ fontSize: '14px', color: '#2e7d32', margin: '0 0 10px 0' }}>TOON (38 tokens)</h4>
            <pre style={{ margin: 0, fontSize: '13px', whiteSpace: 'pre-wrap' }}>{`[2]{id,name,age}:
  1,Alice,30
  2,Bob,25`}</pre>
          </div>
        </div>

        <h3 style={{ fontSize: '18px', margin: '20px 0 10px', color: '#333' }}>When to Use TOON</h3>
        <ul style={{ marginBottom: '15px', paddingLeft: '20px' }}>
          <li style={{ marginBottom: '8px' }}>Sending structured data to ChatGPT, Claude, or other LLMs</li>
          <li style={{ marginBottom: '8px' }}>Reducing API costs by minimizing token usage</li>
          <li style={{ marginBottom: '8px' }}>Improving response times with smaller payloads</li>
          <li style={{ marginBottom: '8px' }}>Making data more readable in prompts</li>
        </ul>
      </div>
      
    </>
  );
};

export default JsonToToonPage;
